import React, { useEffect, useState, useContext } from "react";
import { Button, Input, Label, ButtonGroup } from "reactstrap";
import Alert from "react-s-alert";
import { useHistory } from "react-router-dom";
import PsychometricService from "../../services/PsychometricService";
import { ThemeContext } from "../../contexts/ThemeContext";
import { PsychometricContext } from "../../contexts/PsychometricContext";
import PsychometricTestMainNavbar from './PsychometricTestMainNavbar';
import ErrorBoundary from "../general/ErrorBoundary";
import Message from '../general/Message';

const PsychometricManagementPortal = (props) => {
  const history = useHistory();
  const { theme } = useContext(ThemeContext);
  const {
    signedOrganization,
    setSignedEmail,
    setSignedPassword,
    setSignedOrganization,
    setSignedID,
    setSignedFirstName,
    setSignedLastName,
    setIsStudent,
    isStudent
  } = useContext(PsychometricContext);

  const [orgUser, setOrgUser] = useState({ email: "", password: "", otp: "" });
  const [individualUser, setIndividualUser] = useState({ email: "", password: "", otp: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [isTwoFactorOn, setIsTwoFactorOn] = useState(false);
  const [message, setMessage] = useState({ msgBody: '', msgError: false });

  useEffect(() => {
   
    const params = new URLSearchParams(props.location.search);

    if (props.location?.state?.verificationMessage) {
      setMessage({
        msgBody: props.location.state.verificationMessage,
        msgError: false
      });
      history.replace({ ...history.location, state: {} });
    }
  }, [props.location.search, props.location]);

  const handleOrgChange = (e) => {
    setOrgUser({ ...orgUser, [e.target.name]: e.target.value });
  };


   const handleIndividualChange = (e) => {
    setIndividualUser({ ...individualUser, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, password } = individualUser;
      const nameresponse = await PsychometricService.getPsychometricIndividual(email);
      const response = await PsychometricService.signInIndividual(email, password);

      if (response?.isTwoFactorOn) {
        setIsTwoFactorOn(true);
        setMessage({ msgBody: "Please check your email for the OTP code", msgError: false });
        setIsLoading(false);
        return;
      }

      if (response && nameresponse) {
        setSignedEmail(email);
        setSignedPassword(password);
        setSignedFirstName(nameresponse.user.firstName);
        setSignedLastName(nameresponse.user.lastName);
        setIsStudent(true);

        setMessage({ msgBody: "Individual authenticated successfully", msgError: false });
        history.push("/psychometricIndividualHome");
      } else {
        setMessage({ msgBody: "User does not exist. Please check your credentials or sign up below to create an account", msgError: true });
      }
    } catch (error) {
      setMessage({ 
        msgBody: error?.message?.message || "User does not exist. Please check your credentials or sign up below to create an account", 
        msgError: true 
      });
      console.error("Error authenticating individual:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, password } = orgUser;
      const nameresponse = await PsychometricService.getPsychometricOrganization(email);
      const response = await PsychometricService.signInOrganization(email, password);
      
      if (response?.isTwoFactorOn) {
        setIsTwoFactorOn(true);
        setMessage({ msgBody: "Please check your email for the OTP code", msgError: false });
        setIsLoading(false);
        return;
      }

      if (response && nameresponse) {
        setSignedEmail(email);
        setSignedPassword(password);
        setSignedOrganization(nameresponse.user.name);
        localStorage.setItem('signedOrganization', nameresponse.user.name);

        setMessage({ msgBody: "Organization authenticated successfully", msgError: false });
        setTimeout(() => {
          history.push("/psychometricOrganizationHome");
        }, 100);
      } else {
        setMessage({ msgBody: "Organization not found. Please check your credentials or sign up below to create an account", msgError: true });
      }
    } catch (error) {
      setMessage({ 
        msgBody: error?.message?.message || "Organization does not exist. Please check your credentials or sign up below to create an account", 
        msgError: true 
      });
      console.error("Error authenticating organization:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, otp: inputOtp } = orgUser;
      const storedOtp = orgUser.otp;
      const nameresponse = await PsychometricService.getPsychometricOrganization(email);

      if (inputOtp === storedOtp.toString()) {
        setSignedEmail(email);
        setSignedPassword(orgUser.password);
        setSignedOrganization(nameresponse.user.name);

        Alert.success("Organization authenticated successfully");
        setOrgUser({ email: "", password: "", otp: "" });
        history.push("/psychometricOrganizationHome");
      } else {
        Alert.error("Invalid OTP code. Please try again.");
      }
    } catch (error) {
      Alert.error(error?.message?.msgBody || "Error verifying OTP");
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIndividualOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { email, otp, password } = individualUser;
      const response = await PsychometricService.signInIndividual(email, password, {
        isTwoFactor: true,
        otp
      });

      if (response?.message === 'Sign-in successful') {
        setSignedEmail(email);
        setSignedPassword(password);
        setSignedFirstName(response.individual.firstName);
        setSignedLastName(response.individual.lastName);
        setIsStudent(true);

        Alert.success("Individual authenticated successfully");
        setIndividualUser({ email: "", password: "", otp: "" });
        history.push("/psychometricIndividualHome");
      } else {
        Alert.error(response?.message || "Invalid OTP code. Please try again.");
      }
    } catch (error) {
      Alert.error(error?.message?.msgBody || "Error verifying OTP");
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <ErrorBoundary errorAt="At Psychometric Management Portal Navbar">
        <PsychometricTestMainNavbar />
      </ErrorBoundary>

      <div className="text-center mt-4">
        <h1 style={theme.headingXXL}>Psychometric Management Portal</h1>
      </div>

      <div className="p-3 col-md-4 mt-5 mb-5 offset-md-4" style={theme.form}>
        <ErrorBoundary errorAt="At Management Portal Message">
          {message.msgBody && <Message message={message} />}
        </ErrorBoundary>

        {!activeTab ? (
          <div style={{ paddingLeft: "10%", paddingRight: "10%" }}>
            <h2 style={theme.heading}>Select User Type</h2>
            <div className="mt-4">
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="radio"
                  name="userType"
                  id="individualType"
                  onChange={() => setActiveTab("testTaker")}
                  style={theme.formInput}
                />
                <label className="form-check-label" htmlFor="individualType" style={theme.formBodyText}>
                  Individual
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="userType"
                  id="organizationType"
                  onChange={() => setActiveTab("organization")}
                  style={theme.formInput}
                />
                <label className="form-check-label" htmlFor="organizationType" style={theme.formBodyText}>
                  Organisation
                </label>
              </div>
            </div>
            <div className="text-center mt-4">
              <Button
                type="button"
                color="secondary"
                style={theme.formSubmit}
                className="ml-2"
                onClick={() => history.push('/')}
                >
                Return to Home
              </Button>
            </div>
          </div>
        ) : activeTab === "testTaker" ? (
          <form
            onSubmit={isTwoFactorOn ? handleIndividualOtpSubmit : handleSubmit}
            style={{ paddingLeft: "10%", paddingRight: "10%" }}
          >
            <h2 id="login-heading" style={theme.heading}>
              Individual Sign In
            </h2>
            <br />
            <Label htmlFor="email" style={theme.formBodyText}>
              Email
            </Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={individualUser.email}
              onChange={handleIndividualChange}
              className="form-control"
              required
              style={theme.formInput}
            />
            <br />
            <Label htmlFor="password" style={theme.formBodyText}>
              Password
            </Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={individualUser.password}
              onChange={handleIndividualChange}
              className="form-control"
              required
              style={theme.formInput}
            />
            <br />
            {isTwoFactorOn && (
              <div>
                <Label htmlFor="otp" style={theme.formBodyText}>
                  OTP
                </Label>
                <Input
                  type="text"
                  id="otp"
                  name="otp"
                  value={individualUser.otp}
                  onChange={handleIndividualChange}
                  className="form-control"
                  required
                  style={theme.formInput}
                />
                <br />
              </div>
            )}

            
            <div className="mt-3">
              <Button
                type="button"
                color="secondary"
                onClick={() => setActiveTab(null)}
                className="mr-2"
                style={{ ...theme.formSubmit, marginRight: '10px' }}
              >
                Back
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isLoading}
                style={theme.formSubmit}
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm mr-1"></span>
                ) : null}
                {isTwoFactorOn ? "Verify OTP" : "Sign In"}
              </Button>
            </div>
            <div className="text-end mb-3 mt-3 mt-md-4 mt-lg-5">
                <a href="/psychometricForgotPassword" style={{ color: '#006400' }}>Forgot Password?</a>
            </div>
          </form>
        ) : (
          <form
            onSubmit={isTwoFactorOn ? handleOtpSubmit : handleOrgSubmit}
            style={{ paddingLeft: "10%", paddingRight: "10%" }}
          >
            <h2 id="org-login-heading" style={theme.heading}>
              Organisation Sign In
            </h2>
            <br />
            <Label htmlFor="orgEmail" style={theme.formBodyText}>
              Email
            </Label>
            <Input
              type="email"
              id="orgEmail"
              name="email"
              value={orgUser.email}
              onChange={handleOrgChange}
              className="form-control"
              required
              style={theme.formInput}
            />
            <br />
            <Label htmlFor="password" style={theme.formBodyText}>
              Password
            </Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={orgUser.password}
              onChange={handleOrgChange}
              className="form-control"
              required
              style={theme.formInput}
            />
            <br />
            {isTwoFactorOn && (
              <div>
                <Label htmlFor="otp" style={theme.formBodyText}>
                  OTP
                </Label>
                <Input
                  type="text"
                  id="otp"
                  name="otp"
                  value={orgUser.otp}
                  onChange={handleOrgChange}
                  className="form-control"
                  required
                  style={theme.formInput}
                />
                <br />
              </div>
            )}
            <div className="mt-3">
              <Button
                type="button"
                color="secondary"
                onClick={() => setActiveTab(null)}
                className="mr-2"
                style={{ ...theme.formSubmit, marginRight: '10px' }}
              >
                Back
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isLoading}
                style={theme.formSubmit}
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm mr-1"></span>
                ) : null}
                {isTwoFactorOn ? "Verify OTP" : "Sign In"}
              </Button>
            </div>
            <div className="text-end mb-3 mt-3 mt-md-4 mt-lg-5">
                <a href="/psychometricForgotPassword" style={{ color: '#006400' }}>Forgot Password?</a>
            </div>
          </form>
        )}
        
        {activeTab === 'organization' && (
          <div className="text-center mt-3" style={{ backgroundColor: '#f0f0f0', padding: '10px' }}>
            <p style={theme.formBodyText}>
              New to Psychometric Tests?{' '}
              <a 
                href="/psychometricOrgSignUp" 
                style={{
                  color: '#006400',
                  textDecoration: 'underline',
                  fontWeight: 'bold'
                }}
              >
                Sign up
              </a>
            </p>
          </div>
        )}
        {activeTab === 'testTaker' && (
          <div className="text-center mt-3" style={{ backgroundColor: '#f0f0f0', padding: '10px' }}>
            <p style={theme.formBodyText}>
              New to Psychometric Tests?{' '}
              <a 
                href="/psychometricIndividualSignUp" 
                style={{
                  color: '#006400',
                  textDecoration: 'underline',
                  fontWeight: 'bold'
                }}
              >
                Sign up
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PsychometricManagementPortal;
